import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { getActionableError } from "@/lib/errors";
import Nav from "@/components/Nav";
import ConversationStatusChips, { displayOutcomeLabel } from "@/components/ConversationStatusChips";
import BookmarkSortMenu from "@/components/BookmarkSortMenu";
import FollowUpEmail from "@/components/FollowUpEmail";
import { useAuth } from "@/context/AuthContext";
import { trackProductEvent } from "@/lib/productAnalytics";
import {
  conversationTimestamp,
  formatConversationTime,
  groupCallsByLocalDate,
  localConversationDateKey,
} from "@/lib/conversationDateGroups";
import {
  CONVERSATION_SORT,
  conversationOutcome,
  sortConversationCalls,
} from "@/lib/bookmarkSorting";
import {
  ArrowClockwise,
  ArrowRight,
  Brain,
  Buildings,
  CalendarBlank,
  CheckCircle,
  ClipboardText,
  Clock,
  ClockCounterClockwise,
  MagnifyingGlass,
  Phone,
  Plus,
  ShieldCheck,
  Sparkle,
  Trash,
  User,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const DASHBOARD_BOOKMARK_LINE_TARGETS = Object.freeze({
  wide: 42,
  compact: 36,
  mobile: 30,
});

function formatDur(seconds) {
  if (!seconds) return "—";
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

function firstName(user) {
  const source = user?.name?.trim();
  return source ? source.split(/\s+/)[0] : null;
}

function callIsProcessing(call) {
  return ["queued", "transcribing", "analyzing"].includes(call?.processing?.status);
}

function callPath(call) {
  return callIsProcessing(call) ? "/upload" : `/calls/${call.id}`;
}

function callExplicitlyIneligibleForFollowUp(call) {
  const classification = call?.analysis?.conversation_classification
    || call?.conversation_memory?.conversation_classification
    || {};
  return (
    call?.is_customer_conversation === false
    || classification.is_customer_conversation === false
  );
}

function callSupportsFollowUp(call) {
  return (
    !callIsProcessing(call)
    && Boolean(call?.analysis)
    && !callExplicitlyIneligibleForFollowUp(call)
  );
}

function followUpRelationshipKey(call) {
  const storedKey = String(call?.relationship_id || call?.relationship_key || "").trim();
  if (storedKey) return `relationship:${storedKey}`;

  const customer = String(call?.customer_name || "").trim().toLocaleLowerCase();
  if (customer) return `customer:${customer}`;

  const company = String(call?.company || "").trim().toLocaleLowerCase();
  if (company) return `company:${company}`;

  return `call:${String(call?.id || "")}`;
}

function DashboardFollowUpState({ state }) {
  const config = {
    previous: { label: "Previous call", icon: ClockCounterClockwise },
    analyzing: { label: "Analyzing...", icon: ArrowClockwise },
    unavailable: { label: "Not available", icon: X },
  }[state] || { label: "Not available", icon: X };
  const Icon = config.icon;

  return (
    <span
      className={`dashboard-call-card__follow-up dashboard-call-card__follow-up--state is-${state}`}
      aria-label={config.label}
    >
      <Icon size={14} weight="bold" aria-hidden="true" />
      <span>{config.label}</span>
    </span>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [calls, setCalls] = useState([]);
  const [query, setQuery] = useState(() => sessionStorage.getItem("callsect:dashboard:query") || "");
  const [dateFilter, setDateFilter] = useState(() => sessionStorage.getItem("callsect:dashboard:date") || "");
  const [statusFilter, setStatusFilter] = useState(() => sessionStorage.getItem("callsect:dashboard:status") || "all");
  const [sortOrder, setSortOrder] = useState(() => {
    const stored = sessionStorage.getItem("callsect:dashboard:sort");
    return Object.values(CONVERSATION_SORT).includes(stored) ? stored : CONVERSATION_SORT.NEWEST;
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deletingCallId, setDeletingCallId] = useState(null);
  const [bookmarkOutcomeTrackWidth, setBookmarkOutcomeTrackWidth] = useState(0);
  const [followUpTrackWidth, setFollowUpTrackWidth] = useState(0);
  const callListRef = useRef(null);

  const loadCalls = useCallback(async ({ preserve = false } = {}) => {
    if (!preserve) setLoading(true);
    setLoadError("");
    try {
      const { data } = await api.get("/calls");
      const rows = Array.isArray(data) ? data : [];
      setCalls(rows);
      trackProductEvent("dashboard_viewed", {
        call_count: rows.length,
        section: rows.length === 0 ? "empty" : rows.length === 1 ? "first_call_saved" : "active_library",
      });
    } catch (error) {
      setLoadError(getActionableError(error, "Callsect could not load your conversation library."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCalls();
  }, [loadCalls]);

  const hasProcessingCalls = calls.some(callIsProcessing);

  useEffect(() => {
    if (!hasProcessingCalls) return undefined;
    let cancelled = false;

    const refreshProcessingCalls = async () => {
      try {
        const { data } = await api.get("/calls");
        if (!cancelled && Array.isArray(data)) setCalls(data);
      } catch {
        // A transient refresh failure should not replace a dashboard that already loaded.
      }
    };

    const timer = window.setInterval(refreshProcessingCalls, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [hasProcessingCalls]);

  useEffect(() => {
    sessionStorage.setItem("callsect:dashboard:query", query);
  }, [query]);

  useEffect(() => {
    if (dateFilter) {
      sessionStorage.setItem("callsect:dashboard:date", dateFilter);
    } else {
      sessionStorage.removeItem("callsect:dashboard:date");
    }
  }, [dateFilter]);

  useEffect(() => {
    if (statusFilter && statusFilter !== "all") {
      sessionStorage.setItem("callsect:dashboard:status", statusFilter);
    } else {
      sessionStorage.removeItem("callsect:dashboard:status");
    }
  }, [statusFilter]);

  useEffect(() => {
    sessionStorage.setItem("callsect:dashboard:sort", sortOrder);
  }, [sortOrder]);

  useEffect(() => {
    const saveScroll = () => {
      sessionStorage.setItem("callsect:dashboard:scroll", String(window.scrollY));
    };
    window.addEventListener("scroll", saveScroll, { passive: true });
    return () => {
      saveScroll();
      window.removeEventListener("scroll", saveScroll);
    };
  }, []);

  useEffect(() => {
    if (loading) return undefined;
    const savedScroll = Number(sessionStorage.getItem("callsect:dashboard:scroll") || 0);
    const frame = window.requestAnimationFrame(() => window.scrollTo({ top: savedScroll, left: 0 }));
    return () => window.cancelAnimationFrame(frame);
  }, [loading]);

  const deleteCall = async () => {
    if (!pendingDelete || deletingCallId) return;

    setDeletingCallId(pendingDelete.id);
    try {
      await api.delete(`/calls/${pendingDelete.id}`);
      setCalls((current) => current.filter((call) => call.id !== pendingDelete.id));
      setPendingDelete(null);
      toast.success("Call deleted");
    } catch (error) {
      toast.error(getActionableError(error, "The call could not be deleted."));
    } finally {
      setDeletingCallId(null);
    }
  };

  const normalizedQuery = query.trim().toLowerCase();
  const statusOptions = useMemo(() => {
    const preferred = [
      "In progress",
      "Churn risk",
      "Closing",
      "Ready to move",
      "Stalled",
      "Cool",
      "Recorded",
      "Outside customer workflow",
    ];
    const available = new Set(calls.map(conversationOutcome).filter(Boolean));
    if (statusFilter !== "all") available.add(statusFilter);
    const extras = [...available]
      .filter((status) => !preferred.includes(status))
      .sort((left, right) => left.localeCompare(right));
    return [...preferred.filter((status) => available.has(status)), ...extras];
  }, [calls, statusFilter]);

  const filtered = useMemo(() => {
    const matchingCalls = calls.filter((call) => {
      const matchesQuery = (
        !normalizedQuery
        || (call.title || "").toLowerCase().includes(normalizedQuery)
        || (call.customer_name || "").toLowerCase().includes(normalizedQuery)
        || (call.company || "").toLowerCase().includes(normalizedQuery)
      );
      const matchesDate = (
        !dateFilter
        || localConversationDateKey(conversationTimestamp(call)) === dateFilter
      );
      const matchesStatus = statusFilter === "all" || conversationOutcome(call) === statusFilter;
      return matchesQuery && matchesDate && matchesStatus;
    });

    return sortConversationCalls(matchingCalls, sortOrder);
  }, [calls, dateFilter, normalizedQuery, sortOrder, statusFilter]);


  const customerCalls = useMemo(
    () => calls.filter((call) => call.is_customer_conversation !== false),
    [calls],
  );

  const groupedCalls = useMemo(
    () => groupCallsByLocalDate(filtered),
    [filtered],
  );

  const latestFollowUpCallByRelationship = useMemo(() => {
    const latest = new Map();

    calls.forEach((call, index) => {
      if (callExplicitlyIneligibleForFollowUp(call)) return;

      const key = followUpRelationshipKey(call);
      const rawTimestamp = conversationTimestamp(call);
      const parsedTimestamp = rawTimestamp ? new Date(rawTimestamp).getTime() : Number.NaN;
      const timestamp = Number.isFinite(parsedTimestamp) ? parsedTimestamp : 0;
      const current = latest.get(key);

      if (!current || timestamp > current.timestamp || (timestamp === current.timestamp && index < current.index)) {
        latest.set(key, { id: String(call.id), timestamp, index });
      }
    });

    return latest;
  }, [calls]);

  const renderDashboardFollowUpAction = (call) => {
    if (callExplicitlyIneligibleForFollowUp(call)) {
      return <DashboardFollowUpState state="unavailable" />;
    }

    const latest = latestFollowUpCallByRelationship.get(followUpRelationshipKey(call));
    if (!latest || String(call.id) !== latest.id) {
      return <DashboardFollowUpState state="previous" />;
    }

    if (callIsProcessing(call)) {
      return <DashboardFollowUpState state="analyzing" />;
    }

    if (!callSupportsFollowUp(call)) {
      return <DashboardFollowUpState state="unavailable" />;
    }

    return (
      <FollowUpEmail
        callId={call.id}
        customerName={call.customer_name}
        available={Boolean(call.follow_up_email?.available || call.follow_up_email?.subject)}
        suggestion={call.follow_up_suggestion}
        triggerClassName="dashboard-call-card__follow-up"
        onSuggestionDismissed={(followUpSuggestion) => setCalls((current) => current.map((item) => (
          item.id === call.id
            ? { ...item, follow_up_suggestion: followUpSuggestion }
            : item
        )))}
        onSaved={(followUpEmail) => setCalls((current) => current.map((item) => (
          item.id === call.id
            ? {
                ...item,
                follow_up_email: {
                  available: true,
                  generated_at: followUpEmail.generated_at,
                  updated_at: followUpEmail.updated_at,
                },
              }
            : item
        )))}
      />
    );
  };

  useLayoutEffect(() => {
    const root = callListRef.current;
    if (!root) return undefined;

    let animationFrame = 0;
    let disposed = false;

    const measureOutcomeTrack = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        if (disposed) return;
        const outcomeChips = Array.from(
          root.querySelectorAll(".dashboard-call-card__status .conversation-status-chip--outcome")
        );
        // Measure the natural pill width, not the width after the shared grid
        // track has already constrained it. Using scrollWidth prevents a
        // feedback loop where a long outcome (e.g. “Ready to move”) gets
        // measured too narrowly and the descriptive pill starts on top of it.
        const measuredWidth = Math.ceil(
          outcomeChips.reduce((maximum, chip) => {
            const renderedWidth = chip.getBoundingClientRect().width;
            const naturalWidth = chip.scrollWidth || renderedWidth;
            return Math.max(maximum, renderedWidth, naturalWidth);
          }, 0)
        );
        setBookmarkOutcomeTrackWidth((currentWidth) =>
          Math.abs(currentWidth - measuredWidth) < 1 ? currentWidth : measuredWidth
        );

        const followUpButtons = Array.from(
          root.querySelectorAll(".dashboard-call-card__follow-up")
        );
        const measuredFollowUpWidth = Math.ceil(
          followUpButtons.reduce((maximum, button) => {
            const renderedWidth = button.getBoundingClientRect().width;
            const naturalWidth = button.scrollWidth || renderedWidth;
            return Math.max(maximum, renderedWidth, naturalWidth);
          }, 0)
        );
        setFollowUpTrackWidth((currentWidth) =>
          Math.abs(currentWidth - measuredFollowUpWidth) < 1 ? currentWidth : measuredFollowUpWidth
        );

        // Conversation Bookmarks keep one invariant single-line status row.
        // The widest first pill still defines the shared axis, but the
        // descriptive pill is never pushed onto a second row or split across
        // multiple lines. Clear any legacy stacking marker left by an older
        // render so resizing cannot reintroduce the previous regression.
        root.querySelectorAll(".dashboard-call-card__status .conversation-status-chips").forEach((chips) => {
          chips.removeAttribute("data-bookmark-stack");
        });
      });
    };

    measureOutcomeTrack();

    const resizeObserver = typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(measureOutcomeTrack);

    resizeObserver?.observe(root);
    root
      .querySelectorAll(".dashboard-call-card__status .conversation-status-chip--outcome, .dashboard-call-card__follow-up")
      .forEach((element) => resizeObserver?.observe(element));

    window.addEventListener("resize", measureOutcomeTrack);
    document.fonts?.ready?.then(() => {
      if (!disposed) measureOutcomeTrack();
    });

    return () => {
      disposed = true;
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", measureOutcomeTrack);
      resizeObserver?.disconnect();
    };
  }, [groupedCalls]);

  const clearLibraryFilters = () => {
    setQuery("");
    setDateFilter("");
    setStatusFilter("all");
  };

  const stats = useMemo(() => ({
    total: calls.length,
    thisWeek: calls.filter(
      (call) => new Date(call.occurred_at || call.created_at) > new Date(Date.now() - 7 * 864e5),
    ).length,
    customers: new Set(
      customerCalls.map((call) => call.relationship_id || call.customer_name).filter(Boolean),
    ).size,
    ready: calls.filter((call) => Boolean(call.analysis)).length,
  }), [calls, customerCalls]);

  const name = firstName(user);
  const hasCalls = calls.length > 0;

  return (
    <div className="dashboard-page">
      <Nav />

      <main className="dashboard-shell product-page-enter">
        <header className="dashboard-workspace-header">
          <div>
            <div className="brand-eyebrow">YOUR CALLS</div>
            <h1>{name ? `Welcome back, ${name}.` : "Welcome back."}</h1>
            <p>Review what changed, prepare the next move, or add another call.</p>
          </div>

          <Link
            to="/upload"
            data-testid="new-call-btn"
            className="dashboard-primary-button"
            onClick={() => trackProductEvent("new_call_cta_clicked", { call_count: calls.length, section: "dashboard_header" })}
          >
            <Plus size={16} weight="bold" />
            Upload new call
          </Link>
        </header>

        {loadError ? (
          <LoadFailure message={loadError} onRetry={() => loadCalls()} />
        ) : !loading && !hasCalls ? (
          <FirstCallLaunchpad />
        ) : (
          <>
            <section className="dashboard-stats" aria-label="Call overview">
              <StatCard
                label="Total calls"
                value={stats.total}
                detail="Saved conversations"
                icon={Phone}
                tone="violet"
                testId="stat-total"
              />
              <StatCard
                label="This week"
                value={stats.thisWeek}
                detail="Last 7 days"
                icon={Clock}
                tone="blue"
                testId="stat-week"
              />
              <StatCard
                label="Relationships"
                value={stats.customers}
                detail="Connected customer histories"
                icon={Buildings}
                tone="cyan"
                testId="stat-customers"
              />
              <StatCard
                label="Dissections"
                value={stats.ready}
                detail="Calls with a complete Dissection"
                icon={CheckCircle}
                tone="emerald"
                testId="stat-ready"
              />
            </section>


            <section className="dashboard-library">
              <div className="dashboard-library__toolbar">
                {!loading && calls.length > 0 && (
                  <div className="dashboard-library__filters">
                    <label className="dashboard-search">
                      <MagnifyingGlass size={17} aria-hidden="true" />
                      <input
                        data-testid="search-input"
                        placeholder="Search calls or customers..."
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        aria-label="Search calls, customers, and companies"
                      />
                      {query ? (
                        <button
                          type="button"
                          className="dashboard-search__clear"
                          onClick={() => setQuery("")}
                          aria-label="Clear search"
                        >
                          <X size={14} weight="bold" />
                        </button>
                      ) : null}
                      {query && (
                        <span className="dashboard-search__count">
                          {filtered.length}/{calls.length}
                        </span>
                      )}
                    </label>

                    <div className={`dashboard-date-filter${dateFilter ? " is-active" : ""}`}>
                      <CalendarBlank size={16} aria-hidden="true" />
                      <input
                        type="date"
                        value={dateFilter}
                        onChange={(event) => setDateFilter(event.target.value)}
                        aria-label="Filter conversations by date"
                        data-testid="conversation-date-filter"
                      />
                      {dateFilter && (
                        <button
                          type="button"
                          className="dashboard-date-filter__clear"
                          onClick={() => setDateFilter("")}
                          aria-label="Clear date filter"
                        >
                          <X size={13} weight="bold" />
                        </button>
                      )}
                    </div>

                    <BookmarkSortMenu
                      testId="conversation-sort-filter"
                      className="bookmark-sort-menu__trigger--conversation"
                      filtered={statusFilter !== "all"}
                      summary={statusFilter !== "all"
                        ? `Status: ${displayOutcomeLabel(statusFilter)}`
                        : `Call: ${sortOrder === CONVERSATION_SORT.OLDEST ? "Old → New" : "New → Old"}`}
                      ariaLabel={`Sort and filter conversations. Call: ${sortOrder === CONVERSATION_SORT.OLDEST ? "Old to new" : "New to old"}. Status: ${statusFilter === "all" ? "Any status" : displayOutcomeLabel(statusFilter)}.`}
                      groups={[
                        {
                          label: "Call",
                          value: sortOrder,
                          onValueChange: setSortOrder,
                          options: [
                            { value: CONVERSATION_SORT.NEWEST, label: "New → Old", testId: "conversation-sort-new-old" },
                            { value: CONVERSATION_SORT.OLDEST, label: "Old → New", testId: "conversation-sort-old-new" },
                          ],
                        },
                        {
                          label: "Status",
                          value: statusFilter,
                          onValueChange: setStatusFilter,
                          options: [
                            { value: "all", label: "Any status", testId: "conversation-status-any" },
                            ...statusOptions.map((status) => ({
                              value: status,
                              label: displayOutcomeLabel(status),
                              testId: `conversation-status-${status.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
                            })),
                          ],
                        },
                      ]}
                    />
                  </div>
                )}
              </div>

              {loading ? (
                <div className="dashboard-loading" role="status" aria-live="polite">
                  {[0, 1, 2, 3].map((item) => (
                    <div key={item} className="dashboard-skeleton">
                      <span className="dashboard-skeleton__mark" />
                      <span className="dashboard-skeleton__copy">
                        <i />
                        <i />
                      </span>
                    </div>
                  ))}
                  <span className="sr-only">Loading calls...</span>
                </div>
              ) : filtered.length === 0 ? (
                <div className="dashboard-empty dashboard-empty--search">
                  <div className="dashboard-empty__icon">
                    <MagnifyingGlass size={26} weight="duotone" />
                  </div>
                  <strong>No matching conversations.</strong>
                  <p>Try a different call, customer, company, or date.</p>
                  <button type="button" className="dashboard-secondary-button" onClick={clearLibraryFilters}>
                    Clear filters
                  </button>
                </div>
              ) : (
                <div
                  ref={callListRef}
                  className={`dashboard-call-list${bookmarkOutcomeTrackWidth > 0 ? " has-shared-outcome-track" : ""}${followUpTrackWidth > 0 ? " has-shared-follow-up-track" : ""}`}
                  style={(bookmarkOutcomeTrackWidth > 0 || followUpTrackWidth > 0)
                    ? {
                        ...(bookmarkOutcomeTrackWidth > 0
                          ? { "--dashboard-outcome-track-width": `${bookmarkOutcomeTrackWidth}px` }
                          : {}),
                        ...(followUpTrackWidth > 0
                          ? { "--dashboard-follow-up-track-width": `${followUpTrackWidth}px` }
                          : {}),
                      }
                    : undefined}
                >
                  {groupedCalls.map((group) => (
                    <section
                      key={group.key}
                      className="dashboard-call-group"
                      aria-labelledby={`dashboard-call-group-${group.key}`}
                    >
                      <div className="dashboard-call-group__header">
                        <time
                          id={`dashboard-call-group-${group.key}`}
                          dateTime={group.key === "date-unavailable" ? undefined : group.key}
                        >
                          {group.label}
                        </time>
                        <span aria-hidden="true" />
                      </div>

                      <div className="dashboard-call-group__rows">
                        {group.items.map(({ call, index }) => (
                          <article
                            key={call.id}
                            className="dashboard-call-card"
                            style={{ animationDelay: `${Math.min(index, 8) * 35}ms` }}
                          >
                            <Link
                              to={callPath(call)}
                              state={{ from: "dashboard" }}
                              data-testid={`call-card-${call.id}`}
                              className="dashboard-call-card__main"
                              aria-label={`Open ${call.title || "call"}`}
                            >
                              <span className="dashboard-call-card__mark" aria-hidden="true">
                                <Phone size={18} weight="duotone" />
                              </span>

                              <span className="dashboard-call-card__content">
                                <span className="dashboard-call-card__topline">
                                  <span
                                    className="dashboard-call-card__title"
                                    title={call.title || "Untitled call"}
                                  >
                                    {call.title || "Untitled call"}
                                  </span>
                                </span>
                                <span className="dashboard-call-card__meta">
                                  {call.customer_name && (
                                    <span className="dashboard-call-card__customer">
                                      <User size={13} weight="fill" />
                                      {call.customer_name}
                                    </span>
                                  )}
                                  {call.company && (
                                    <span className="dashboard-call-card__company">
                                      <Buildings size={13} weight="fill" />
                                      {call.company}
                                    </span>
                                  )}
                                  <span className="dashboard-call-card__duration">
                                    <Clock size={13} weight="fill" />
                                    {formatDur(call.duration)}
                                  </span>
                                </span>
                              </span>

                              <span className="dashboard-call-card__status">
                                <ConversationStatusChips
                                  outcome={call.conversation_status?.outcome}
                                  stage={call.conversation_status?.negotiation_stage}
                                  bookmarkLineTargets={DASHBOARD_BOOKMARK_LINE_TARGETS}
                                />
                              </span>

                              <time
                                className="dashboard-call-card__date"
                                dateTime={conversationTimestamp(call) || undefined}
                              >
                                {formatConversationTime(conversationTimestamp(call))}
                              </time>
                            </Link>

                            {renderDashboardFollowUpAction(call)}

                            {!callIsProcessing(call) && (
                              <button
                                type="button"
                                className="dashboard-call-card__delete"
                                onClick={() => setPendingDelete(call)}
                                data-testid={`delete-call-${call.id}`}
                                aria-label={`Delete ${call.title || "call"}`}
                                title={`Delete ${call.title || "call"}`}
                              >
                                <Trash size={16} weight="bold" aria-hidden="true" />
                              </button>
                            )}

                            <Link
                              to={callPath(call)}
                              state={{ from: "dashboard" }}
                              className="dashboard-call-card__open"
                              aria-label={`Open ${call.title || "call"}`}
                            >
                              <ArrowRight size={16} weight="bold" aria-hidden="true" />
                            </Link>
                          </article>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              )}
            </section>
          </>
        )}

      </main>

      {pendingDelete && (
        <div
          className="call-detail-dialog-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="dashboard-delete-call-title"
          data-testid="dashboard-delete-call-dialog"
        >
          <div className="call-detail-dialog dashboard-delete-dialog">
            <div className="call-detail-dialog__header">
              <div>
                <div className="call-detail-dialog__danger">
                  <Trash size={20} weight="fill" />
                </div>
                <h2 id="dashboard-delete-call-title">Delete this call?</h2>
              </div>
              <button
                type="button"
                onClick={() => setPendingDelete(null)}
                disabled={Boolean(deletingCallId)}
                aria-label="Close"
                className="call-detail-dialog__close"
              >
                <X size={18} />
              </button>
            </div>
            <p>
              <strong>{pendingDelete.title || "Untitled call"}</strong>
              {pendingDelete.customer_name ? ` for ${pendingDelete.customer_name}` : ""} will be permanently removed, including its transcript and Dissection
              {pendingDelete.is_customer_conversation !== false ? ", CRM Notes and Coaching" : ""}.
            </p>
            <small>This action cannot be undone.</small>
            <div className="call-detail-dialog__actions">
              <button
                type="button"
                onClick={() => setPendingDelete(null)}
                disabled={Boolean(deletingCallId)}
                className="call-detail-secondary-button"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={deleteCall}
                disabled={Boolean(deletingCallId)}
                data-testid="dashboard-confirm-delete-call"
                className="call-detail-danger-button"
              >
                <Trash size={15} weight="fill" />
                {deletingCallId ? "Deleting..." : "Delete call"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FirstCallLaunchpad() {
  const outcomes = [
    {
      icon: Brain,
      label: "Understand",
      title: "See what actually changed",
      copy: "Separate evidence, objections, intent, risk, and decision dynamics without rereading the call.",
    },
    {
      icon: ClipboardText,
      label: "Document",
      title: "Leave with the CRM note ready",
      copy: "Generate documentation in your own voice and carry the relationship context forward.",
    },
    {
      icon: Sparkle,
      label: "Improve",
      title: "Know the strongest next move",
      copy: "Turn the call into practical coaching grounded in what the customer actually said.",
    },
  ];

  return (
    <section className="dashboard-first-call" data-testid="dashboard-first-call">
      <div className="dashboard-first-call__hero">
        <div>
          <div className="dashboard-section-label">Start here</div>
          <h2>Your first call should give you a clear win.</h2>
          <p>
            Upload one customer call you know well. Callsect will show you what changed, what to write, and how to improve the next move — in one connected result.
          </p>
          <div className="dashboard-first-call__trust">
            <span><ShieldCheck size={16} weight="fill" /> No raw audio retained</span>
            <span><CheckCircle size={16} weight="fill" /> Multilingual calls supported</span>
          </div>
        </div>

        <Link
          to="/upload"
          data-testid="empty-upload-btn"
          className="dashboard-primary-button dashboard-primary-button--hero"
          onClick={() => trackProductEvent("first_call_cta_clicked", { call_count: 0, section: "dashboard_empty" })}
        >
          <Plus size={17} weight="bold" />
          Upload my first call
          <ArrowRight size={16} weight="bold" />
        </Link>
      </div>

      <div className="dashboard-first-call__outcomes">
        {outcomes.map(({ icon: Icon, label, title, copy }) => (
          <article key={title}>
            <span className="dashboard-first-call__icon" aria-hidden="true"><Icon size={21} weight="duotone" /></span>
            <small>{label}</small>
            <strong>{title}</strong>
            <p>{copy}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function LoadFailure({ message, onRetry }) {
  return (
    <section className="product-state product-state--error" role="alert">
      <span className="product-state__icon"><ArrowClockwise size={24} weight="duotone" /></span>
      <div>
        <strong>Your conversations could not be loaded.</strong>
        <p>{message}</p>
      </div>
      <button type="button" onClick={onRetry}>
        <ArrowClockwise size={16} weight="bold" />
        Try again
      </button>
    </section>
  );
}

function StatCard({ label, value, detail, icon: Icon, tone, testId }) {
  return (
    <article className={`dashboard-stat-card is-${tone}`} data-testid={testId}>
      <div className="dashboard-stat-card__head">
        <span className="dashboard-stat-card__icon">
          <Icon size={20} weight="duotone" />
        </span>
        <span className="dashboard-stat-card__label">{label}</span>
      </div>
      <strong>{value}</strong>
      <span className="dashboard-stat-card__detail">{detail}</span>
    </article>
  );
}
